/**
 * Auth API — 用户登录/注册/验证码
 *
 * 接口对照后端文档:
 *   POST /discourse/api/v1/user/login      — 登录（用户名+密码 或 手机+验证码）
 *   POST /discourse/api/v1/user/register   — 注册（用户名+密码+手机+验证码）
 *   POST /discourse/api/v1/user/send-code  — 发送短信验证码
 *   GET  /discourse/api/v1/user/profile    — 获取当前登录用户信息
 */

import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  SendCodeRequest,
  SendCodeResponse,
  UserInfo,
  OAuthProvider,
  OAuthRedirectResponse,
} from "@zrt-ai-office/types";
import type { SlideCaptchaData } from "@zrt-ai-office/types";
import {
  apiFetch,
  ApiError,
  setToken,
  setAuthTokens,
  setStoredUser,
  removeToken,
} from "./client";

// Re-export types for consumers that import from this file
export type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  SendCodeRequest,
  SendCodeResponse,
};
export type { UserInfo as UserInfoResponse };
export type { SlideCaptchaData };
export type { OAuthProvider };
export { ApiError };

/**
 * 登录失败但后端要求下次携带验证码时抛出此错误。
 * 调用方应当弹出滑块验证弹窗，通过后携带验证码重新调用 loginWithPassword。
 */
export class LoginNeedCaptchaError extends Error {
  constructor(public readonly msg: string) {
    super(msg);
    this.name = "LoginNeedCaptchaError";
  }
}

/**
 * 注册时后端要求滑块验证码时抛出此错误。
 * 调用方应当弹出滑块验证弹窗，通过后携带验证码参数重新调用 register。
 */
export class RegisterNeedCaptchaError extends Error {
  constructor(public readonly msg: string) {
    super(msg);
    this.name = "RegisterNeedCaptchaError";
  }
}

// ── API functions ──

/**
 * 获取滑动验证码
 * GET /discourse/api/v1/user/slide-captcha
 */
export async function getSlideCaptcha(): Promise<SlideCaptchaData> {
  return apiFetch<SlideCaptchaData>("/discourse/api/v1/user/slide-captcha", {
    method: "GET",
    auth: false,
  });
}

/**
 * 校验滑动验证码
 * POST /discourse/api/v1/user/verify-slide-captcha
 * 返回是否验证通过；验证通过后调用方应缓存参数，
 * 在后续业务请求（如登录）时再次携带参数提交。
 */
export async function verifySlideCaptcha(params: {
  captchaId: string;
  pointX: number;
  pointY: number;
}): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(
    "/discourse/api/v1/user/verify-slide-captcha",
    {
      method: "POST",
      body: params,
      auth: false,
    },
  );
}

/**
 * 发送短信验证码（需携带滑块验证信息）
 */
export async function sendSmsCode(
  phone: string,
  codeType: "register" | "login" | "bind",
  captcha?: { captchaId: string; pointX: number; pointY: number },
): Promise<SendCodeResponse> {
  const body = captcha
    ? { phone, codeType, captchaType: "slide", ...captcha }
    : { phone, codeType };
  return apiFetch<SendCodeResponse>("/discourse/api/v1/user/send-code", {
    method: "POST",
    body,
    auth: false,
  });
}

/**
 * 绑定手机号
 * POST /discourse/api/v1/user/bind-phone  body: { phone, code }
 */
export async function bindPhone(
  phone: string,
  code: string,
): Promise<{ code: number; msg: string }> {
  return apiFetch<{ code: number; msg: string }>(
    "/discourse/api/v1/user/bind-phone",
    {
      method: "POST",
      body: { phone, code },
    },
  );
}

/** 滑块验证码参数 */
export interface SlideCaptchaParams {
  captchaId: string;
  pointX: number;
  pointY: number;
}

/**
 * 用户名+密码登录（可附带滑块验证码）
 * 若后端返回 needCaptcha=true，会抛出 LoginNeedCaptchaError，
 * 调用方应弹出滑块验证后携带验证码参数重新调用本函数。
 */
export async function loginWithPassword(
  username: string,
  password: string,
  captcha?: SlideCaptchaParams,
): Promise<LoginResponse> {
  const body: LoginRequest = captcha
    ? { username, password, captchaType: "slide", ...captcha }
    : { username, password };
  try {
    const res = await apiFetch<LoginResponse>("/discourse/api/v1/user/login", {
      method: "POST",
      body,
      auth: false,
    });
    // Persist token & user
    setAuthTokens(res.token, res.refreshToken);
    setStoredUser({
      userId: res.userInfo.id,
      username: res.userInfo.username,
      nick: res.userInfo.nick || undefined,
      avatar: res.userInfo.avatar || undefined,
    });
    return res;
  } catch (err) {
    if (err instanceof ApiError) {
      // 后端在登录失败时也可能在 data 中返回 needCaptcha=true，
      // 此时需要弹出滑块验证，而非直接显示密码错误
      const data = err.body as { needCaptcha?: boolean } | null | undefined;
      if (data?.needCaptcha) {
        throw new LoginNeedCaptchaError(err.statusText || "用户名或密码错误");
      }
    }
    throw err;
  }
}

/**
 * 手机+验证码登录
 */
export async function loginWithPhone(
  phone: string,
  code: string,
): Promise<LoginResponse> {
  const res = await apiFetch<LoginResponse>("/discourse/api/v1/user/login", {
    method: "POST",
    body: { phone, code } satisfies LoginRequest,
    auth: false,
  });
  setAuthTokens(res.token, res.refreshToken);
  setStoredUser({
    userId: res.userInfo.id,
    username: res.userInfo.username,
    nick: res.userInfo.nick || undefined,
    phone,
    avatar: res.userInfo.avatar || undefined,
  });
  return res;
}

/**
 * 用户注册（用户名+密码流程可附带滑块验证码）
 * 若后端返回 needCaptcha=true，会抛出 RegisterNeedCaptchaError，
 * 调用方应弹出滑块验证后携带验证码参数重新调用本函数。
 */
export async function register(
  data: RegisterRequest,
  captcha?: SlideCaptchaParams,
): Promise<RegisterResponse> {
  const body: RegisterRequest = captcha
    ? { ...data, captchaType: "slide", ...captcha }
    : data;
  try {
    return await apiFetch<RegisterResponse>("/discourse/api/v1/user/register", {
      method: "POST",
      body,
      auth: false,
    });
  } catch (err) {
    if (err instanceof ApiError) {
      const d = err.body as { needCaptcha?: boolean } | null | undefined;
      if (d?.needCaptcha) {
        throw new RegisterNeedCaptchaError(
          err.statusText || "注册失败，请完成验证",
        );
      }
    }
    throw err;
  }
}

/**
 * 获取当前用户信息（需要 JWT）
 */
export async function getUserInfo(): Promise<UserInfo> {
  return apiFetch<UserInfo>("/discourse/api/v1/user/profile", {
    method: "GET",
  });
}

/**
 * 登出 — 清除本地 token
 */
export function logout() {
  removeToken();
}

/**
 * 修改用户昵称
 * PUT /discourse/api/v1/user/profile  body: { nick }
 */
export async function updateNickname(
  nickname: string,
): Promise<{ nick: string }> {
  await apiFetch<{ success: boolean; message: string }>(
    "/discourse/api/v1/user/profile",
    {
      method: "PUT",
      body: { nick: nickname },
    },
  );
  return { nick: nickname };
}

/**
 * 上传用户头像
 *
 * 流程：
 *   1. 预签名上传文件到 MinIO → 获取 accessUrl
 *   2. PUT /discourse/api/v1/user/profile { avatar: accessUrl } → 更新头像
 */
export async function uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
  const { uploadFile } = await import("./upload");

  // Step 1: 预签名上传
  const accessUrl = await uploadFile(file);

  // Step 2: 更新用户头像字段
  await apiFetch<{ success: boolean; message: string }>(
    "/discourse/api/v1/user/profile",
    {
      method: "PUT",
      body: { avatar: accessUrl },
    },
  );

  return { avatarUrl: accessUrl };
}

/**
 * 获取 OAuth 第三方登录跳转 URL
 *
 * GET /discourse/api/v1/user/oauth/redirect?type=github|google|wechat&redirect=<currentUrl>
 * - github / google / wechat: 返回授权跳转 URL，前端直接 window.location.href 跳转
 * redirect 参数会被后端透传到 OAuth 回调，完成授权后携带 token 跳回该地址
 */
export async function getOAuthRedirectUrl(
  type: OAuthProvider,
  redirect: string,
): Promise<OAuthRedirectResponse> {
  const params = new URLSearchParams({ type, redirect });
  return apiFetch<OAuthRedirectResponse>(
    `/discourse/api/v1/user/oauth/redirect?${params.toString()}`,
    {
      method: "GET",
      auth: false,
    },
  );
}

/**
 * 处理 OAuth 回调
 *
 * 后端把 token / refreshToken / userInfo 直接编码在回调 URL 的 query 参数里，
 * 前端无需再调接口，直接从参数中取值持久化即可。
 *
 * @param token       access token
 * @param refreshToken  refresh token
 * @param userInfo    已解码的用户信息对象
 */
export function handleOAuthCallback(
  token: string,
  refreshToken: string,
  userInfo: UserInfo,
): void {
  setAuthTokens(token, refreshToken)
  setStoredUser({
    userId: userInfo.id,
    username: userInfo.username,
    nick: userInfo.nick || undefined,
    phone: userInfo.phone || undefined,
    email: userInfo.email || undefined,
    avatar: userInfo.avatar || undefined,
  })
}

/** @deprecated 使用 handleOAuthCallback 替代 */
export async function handleOAuthToken(token: string, refreshToken?: string): Promise<UserInfo> {
  if (refreshToken) {
    setAuthTokens(token, refreshToken);
  } else {
    setToken(token);
  }
  const userInfo = await apiFetch<UserInfo>("/discourse/api/v1/user/profile", {
    method: "GET",
  });
  setStoredUser({
    userId: userInfo.id,
    username: userInfo.username,
    nick: userInfo.nick || undefined,
    phone: userInfo.phone || undefined,
    email: userInfo.email || undefined,
    avatar: userInfo.avatar || undefined,
  });
  return userInfo;
}
